/**
 * SubscriptionAggregator — pure aggregation + debounced diff for multi-
 * source ticker subscriptions. Used by `hydraWorker.ts` to union each
 * tab's declared orderbook needs and reconcile them against the upstream
 * Hydra subscription.
 *
 * Transport-agnostic: callers supply subscribe/unsubscribe callbacks so
 * the same aggregator shape works for SharedWorker + direct-WS paths
 * and is trivial to unit-test.
 */

export interface AggregatorCallbacks {
  /** Called with a non-empty list of tickers that should be added to the
   *  upstream subscription. Batched per reconcile invocation. */
  subscribe: (tickers: string[]) => void;
  /** Called with a single ticker when its grace timer fires and the
   *  ticker is still not in the union (i.e. really gone). */
  unsubscribe: (ticker: string) => void;
}

export interface AggregatorTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

/** Grace before an orphaned orderbook ticker is unsubscribed upstream —
 *  the flicker window (g1xl-v2#1053 route transactions, 2026-10-02): books
 *  for a page you left stop within it, and a quick toggle between views
 *  that share tickers still sends nothing. Was 30 s, which kept a left
 *  page's books streaming for half a minute on every navigation. */
export const ORDERBOOK_UNSUBSCRIBE_GRACE_MS = 1_500;

export interface AggregatorOptions {
  /** Grace period before an orphaned ticker is actually unsubscribed, so
   *  quick toggles between views that share tickers don't churn.
   *  Defaults to ORDERBOOK_UNSUBSCRIBE_GRACE_MS. */
  unsubscribeGraceMs?: number;
  /** Timer injection for tests. Defaults to global setTimeout/clearTimeout. */
  timers?: AggregatorTimers;
}

export class SubscriptionAggregator<SourceId> {
  private perSourceNeeds = new Map<SourceId, Set<string>>();
  private upstreamTickers = new Set<string>();
  private pendingUnsubscribe = new Map<string, unknown>();
  private readonly graceMs: number;
  private readonly timers: AggregatorTimers;

  constructor(private cb: AggregatorCallbacks, opts: AggregatorOptions = {}) {
    this.graceMs = opts.unsubscribeGraceMs ?? ORDERBOOK_UNSUBSCRIBE_GRACE_MS;
    this.timers = opts.timers ?? {
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
    };
  }

  /** Replace a source's declared ticker needs. Empty array drops the
   *  source entirely (same effect as `removeSource`). */
  setNeeds(source: SourceId, tickers: Iterable<string>): void {
    const set = new Set(tickers);
    if (set.size === 0) this.perSourceNeeds.delete(source);
    else this.perSourceNeeds.set(source, set);
    this.reconcile();
  }

  /** Drop a source (e.g. on tab disconnect). Equivalent to `setNeeds(s, [])`. */
  removeSource(source: SourceId): void {
    this.perSourceNeeds.delete(source);
    this.reconcile();
  }

  /** Current union across all sources. */
  union(): Set<string> {
    const out = new Set<string>();
    for (const set of this.perSourceNeeds.values()) {
      for (const t of set) out.add(t);
    }
    return out;
  }

  /** What upstream thinks we're subscribed to right now. */
  currentUpstream(): Set<string> {
    return new Set(this.upstreamTickers);
  }

  /** Clear upstream + pending state (e.g. on WS reconnect). Does NOT
   *  touch `perSourceNeeds`. Follow with `reconcile()` (or any other
   *  operation that calls it) to re-subscribe from scratch. */
  resetUpstream(): void {
    this.upstreamTickers.clear();
    for (const handle of this.pendingUnsubscribe.values()) this.timers.clearTimeout(handle);
    this.pendingUnsubscribe.clear();
  }

  /** Force a reconcile pass without changing needs (useful after
   *  `resetUpstream()` when re-bootstrapping the upstream connection). */
  reconcile(): void {
    const desired = this.union();

    const toAdd: string[] = [];
    for (const ticker of desired) {
      if (!this.upstreamTickers.has(ticker)) toAdd.push(ticker);
      const pending = this.pendingUnsubscribe.get(ticker);
      if (pending !== undefined) {
        this.timers.clearTimeout(pending);
        this.pendingUnsubscribe.delete(ticker);
      }
    }

    for (const ticker of this.upstreamTickers) {
      if (desired.has(ticker)) continue;
      if (this.pendingUnsubscribe.has(ticker)) continue;
      const handle = this.timers.setTimeout(() => {
        this.pendingUnsubscribe.delete(ticker);
        if (this.union().has(ticker)) return; // re-added during grace
        this.upstreamTickers.delete(ticker);
        this.cb.unsubscribe(ticker);
      }, this.graceMs);
      this.pendingUnsubscribe.set(ticker, handle);
    }

    if (toAdd.length > 0) {
      for (const t of toAdd) this.upstreamTickers.add(t);
      this.cb.subscribe(toAdd);
    }
  }
}
